/**
 * P1-D: 评测面板 — 用例管理 + 触发评测 + 报告查看
 * 轮询 /api/evaluation/cases 与 /api/evaluation/reports
 */
import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../services/apiClient';
import { FlaskConical, Plus, Trash2, Play, Loader2, CheckCircle2, XCircle, ClipboardList, BarChart3 } from 'lucide-react';

interface EvalCase {
  id: string;
  name: string;
  input: string;
  expectedOutput?: string;
  expectedKeywords?: string[];
  expectedTools?: string[];
  scoringMethods: string[];
}

interface EvalCaseResult {
  caseId: string;
  caseName: string;
  passed: boolean;
  score: number;
  actualOutput: string;
  actualTools: string[];
  details: Record<string, number>;
  error?: string;
}

interface EvalReport {
  id: string;
  name: string;
  status: 'running' | 'completed' | 'failed';
  totalCases: number;
  passedCount: number;
  avgScore: number;
  results: EvalCaseResult[];
  error?: string;
  updatedAt: string;
}

export function EvaluationPanel({ t }: { t: any }) {
  const [cases, setCases] = useState<EvalCase[]>([]);
  const [reports, setReports] = useState<EvalReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState('');
  const [selectedReport, setSelectedReport] = useState<EvalReport | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', input: '', expectedKeywords: '', expectedOutput: '', scoringMethods: 'keyword' });

  const load = useCallback(async () => {
    try {
      const [casesRes, reportsRes] = await Promise.all([
        apiFetch('/api/evaluation/cases', { credentials: 'include' }),
        apiFetch('/api/evaluation/reports', { credentials: 'include' }),
      ]);
      const casesData = await casesRes.json().catch(() => ({}));
      const reportsData = await reportsRes.json().catch(() => ({}));
      if (casesRes.ok) setCases(casesData.cases || []);
      if (reportsRes.ok) setReports(reportsData.reports || []);
    } catch (e: unknown) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(timer);
  }, [load]);

  const handleCreate = useCallback(async () => {
    if (!form.input.trim()) { setMsg('请输入用例输入'); return; }
    setMsg('');
    try {
      const res = await apiFetch('/api/evaluation/cases', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name || '未命名用例',
          input: form.input,
          expectedOutput: form.expectedOutput || undefined,
          expectedKeywords: form.expectedKeywords ? form.expectedKeywords.split(',').map(s => s.trim()).filter(Boolean) : undefined,
          scoringMethods: form.scoringMethods.split(',').map(s => s.trim()).filter(Boolean),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '创建失败');
      setMsg('用例已创建');
      setForm({ name: '', input: '', expectedKeywords: '', expectedOutput: '', scoringMethods: 'keyword' });
      setShowForm(false);
      await load();
    } catch (e: unknown) {
      setMsg(e instanceof Error ? e.message : String(e));
    }
  }, [form, load]);

  const handleDelete = useCallback(async (id: string) => {
    try {
      await apiFetch(`/api/evaluation/cases/${id}`, { method: 'DELETE', credentials: 'include' });
      await load();
    } catch (e: unknown) {
      setMsg(e instanceof Error ? e.message : String(e));
    }
  }, [load]);

  const handleRun = useCallback(async () => {
    setRunning(true);
    setMsg('');
    try {
      const res = await apiFetch('/api/evaluation/run', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '触发失败');
      setMsg(`评测已启动：${data.caseCount} 个用例 (${data.provider}/${data.model})`);
      await load();
    } catch (e: unknown) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  }, [load]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-white/10 bg-white/[0.03] px-5 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-cyan-300">
            <FlaskConical size={15} /> {t?.evalTitle || '评测体系'}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setShowForm(v => !v)} className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2.5 py-1.5 text-[11px] font-bold text-white/70 hover:bg-white/10">
              <Plus size={12} /> {t?.newCase || '新建用例'}
            </button>
            <button type="button" onClick={() => void handleRun()} disabled={running || cases.length === 0} className="inline-flex items-center gap-1 rounded-md border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-1.5 text-[11px] font-bold text-cyan-200 hover:bg-cyan-400/20 disabled:opacity-50">
              {running ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} {t?.runEval || '触发评测'}
            </button>
          </div>
        </div>
        {msg && <div className="mt-2 rounded-md border border-cyan-400/20 bg-cyan-400/10 px-3 py-1.5 text-[11px] text-cyan-200">{msg}</div>}
      </div>

      {showForm && (
        <div className="shrink-0 border-b border-white/10 bg-black/20 px-5 py-3 space-y-2">
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="用例名称" className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white placeholder:text-white/30" />
          <textarea value={form.input} onChange={e => setForm(f => ({ ...f, input: e.target.value }))} placeholder="输入（用户消息）" rows={2} className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white placeholder:text-white/30" />
          <input value={form.expectedKeywords} onChange={e => setForm(f => ({ ...f, expectedKeywords: e.target.value }))} placeholder="期望关键词（逗号分隔）" className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white placeholder:text-white/30" />
          <input value={form.expectedOutput} onChange={e => setForm(f => ({ ...f, expectedOutput: e.target.value }))} placeholder="期望输出（语义评分用，可选）" className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white placeholder:text-white/30" />
          <div className="flex items-center gap-2">
            <input value={form.scoringMethods} onChange={e => setForm(f => ({ ...f, scoringMethods: e.target.value }))} placeholder="评分方法 keyword,semantic,tool" className="flex-1 rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white placeholder:text-white/30" />
            <button type="button" onClick={() => void handleCreate()} className="rounded-md border border-emerald-400/30 bg-emerald-400/10 px-3 py-1.5 text-[11px] font-bold text-emerald-200 hover:bg-emerald-400/20">保存</button>
          </div>
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-2 gap-3 overflow-y-auto p-4 custom-scrollbar">
        {/* 左：用例列表 */}
        <div className="min-h-0 space-y-2">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-white/40"><ClipboardList size={12} /> 用例 ({cases.length})</div>
          {cases.length === 0 && <div className="rounded-lg border border-dashed border-white/10 px-3 py-6 text-center text-xs text-white/35">{loading ? '加载中...' : '暂无用例，点右上角新建'}</div>}
          {cases.map(c => (
            <div key={c.id} className="rounded-lg border border-white/8 bg-white/[0.03] px-3 py-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-bold text-white/80">{c.name}</div>
                  <div className="mt-0.5 truncate text-[11px] text-white/45">{c.input}</div>
                  <div className="mt-0.5 text-[10px] text-white/30">评分: {c.scoringMethods?.join('/') || 'keyword'}</div>
                </div>
                <button type="button" onClick={() => void handleDelete(c.id)} className="shrink-0 rounded p-1 text-white/35 hover:bg-red-500/10 hover:text-red-300"><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>

        {/* 右：报告列表 + 详情 */}
        <div className="min-h-0 space-y-2">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-white/40"><BarChart3 size={12} /> 报告 ({reports.length})</div>
          {reports.length === 0 && <div className="rounded-lg border border-dashed border-white/10 px-3 py-6 text-center text-xs text-white/35">暂无报告，触发评测后生成</div>}
          {reports.map(r => {
            const active = selectedReport?.id === r.id;
            const passRate = r.totalCases > 0 ? Math.round((r.passedCount / r.totalCases) * 100) : 0;
            return (
              <button key={r.id} type="button" onClick={() => setSelectedReport(active ? null : r)} className={`w-full rounded-lg border px-3 py-2 text-left transition-colors ${active ? 'border-cyan-400/30 bg-cyan-400/10' : 'border-white/8 bg-white/[0.03] hover:bg-white/[0.06]'}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-xs font-bold text-white/80">{r.name}</span>
                  {r.status === 'running' ? <Loader2 size={12} className="animate-spin text-sky-300" /> : r.status === 'completed' ? <CheckCircle2 size={12} className="text-emerald-300" /> : <XCircle size={12} className="text-red-300" />}
                </div>
                <div className="mt-1 flex items-center gap-2 text-[10px] text-white/40">
                  <span>通过 {r.passedCount}/{r.totalCases} ({passRate}%)</span>
                  <span>平均分 {r.avgScore}</span>
                </div>
                {active && r.results && r.results.length > 0 && (
                  <div className="mt-2 space-y-1 border-t border-white/10 pt-2">
                    {r.results.map(res => (
                      <div key={res.caseId} className="text-[10px]">
                        <div className="flex items-center gap-1">
                          {res.passed ? <CheckCircle2 size={10} className="text-emerald-300" /> : <XCircle size={10} className="text-red-300" />}
                          <span className="text-white/60">{res.caseName}</span>
                          <span className="text-white/30">分 {res.score}</span>
                        </div>
                        {res.error && <div className="ml-4 text-red-300">{res.error}</div>}
                        {res.actualOutput && <div className="ml-4 truncate text-white/35">答: {res.actualOutput}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
